// src/pages/pengamatan/buah-alami/detail-tabs/TonaseBuahPerPlotTab.jsx
import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper
} from '@mui/material';
import TonaseBuahPerHaPerPlotTableHeader from './TonaseBuahPerHaPerPlotTableHeader';

const TonaseBuahPerPlotTab = ({ pengamatanData, filteredPlots }) => {
    // SALIN PERSIS semua fungsi dari TonaseBuahPerHaTab.jsx
    const calculateFaseBungaPercentage = (plotData, fieldName) => {
        const faseBunga = plotData.data_entry?.fase_bunga || {};
        const total = parseFloat(plotData.data_entry?.total_ba) || 0;
        const fieldValue = parseFloat(faseBunga[fieldName]) || 0;
        
        if (total > 0) {
            return (fieldValue / total) * 100;
        }
        return 0;
    };

    const calculateTanamanPercentage = (plotData, fieldName) => {
        const tanaman = plotData.data_entry?.tanaman || {};
        const penyakit = plotData.data_entry?.tanaman?.penyakit_tanaman || {};
        const total = parseFloat(plotData.data_entry?.total_ba) || 0;
        
        let fieldValue = 0;
        
        if (fieldName.includes('penyakit_')) {
            const penyakitField = fieldName.replace('penyakit_', '');
            fieldValue = parseFloat(penyakit[`${penyakitField}_pt_ba`]) || 0;
        } else {
            fieldValue = parseFloat(tanaman[`${fieldName}_t_ba`]) || 0;
        }
        
        if (total > 0) {
            return (fieldValue / total) * 100;
        }
        return 0;
    };

    const calculateBuahAlamiMatiPercentage = (plotData, fieldName) => {
        const dataEntry = plotData.data_entry || {};
        const total = parseFloat(dataEntry.total_ba) || 0;
        
        let fieldValue = 0;
        
        if (fieldName === 'buah_alami_ba') {
            fieldValue = parseFloat(dataEntry.buah_alami_ba) || 0;
        } else if (fieldName === 'mati_ba') {
            fieldValue = parseFloat(dataEntry.mati_ba) || 0;
        }
        
        if (total > 0) {
            return (fieldValue / total) * 100;
        }
        return 0;
    };

    // FUNGSI formatAngkaExcel
    const formatAngkaExcel = (angka) => {
        if (!angka || angka === '-' || isNaN(parseFloat(angka))) return '-';
        
        const num = parseFloat(angka);
        
        if (Math.abs(num) < 0.0001) {
            return '-';
        }
        
        if (num >= 1) {
            const formatted = num.toFixed(3);
            const parts = formatted.split('.');
            const integerPart = parts[0];
            const decimalPart = parts[1] || '';
            const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
            return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
        } else {
            const decimalPart = num.toFixed(3).split('.')[1];
            if (decimalPart === '000') {
                return '-';
            }
            return decimalPart;
        }
    };

    // FUNGSI BARU: Format khusus untuk total (2 digit di belakang koma)
    const formatAngkaTotal = (angka) => {
        if (!angka || angka === '-' || isNaN(parseFloat(angka))) return '-';
        
        const num = parseFloat(angka);
        
        if (Math.abs(num) < 0.0001) {
            return '-';
        }
        
        // Format dengan 2 digit di belakang koma
        const formatted = num.toFixed(2);
        const parts = formatted.split('.');
        const integerPart = parts[0];
        const decimalPart = parts[1] || '';
        
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        
        return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
    };

    // FUNGSI BARU: Format khusus untuk PJP (tanpa desimal)
    const formatPJP = (angka) => {
        if (!angka || angka === '-' || isNaN(parseFloat(angka))) return '-';
        
        const num = parseFloat(angka);
        
        // Format tanpa desimal, langsung bulatkan
        const rounded = Math.round(num);
        return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    // FUNGSI UNTUK MENGHITUNG POPULASI PER HA (SAMA DENGAN TonaseBuahPerHaTab)
    const calculatePopulationPerHa = () => {
        const plotsToUse = filteredPlots && filteredPlots.length > 0 
            ? filteredPlots 
            : (pengamatanData.plot ? Object.entries(pengamatanData.plot).map(([plotKey, plotData]) => ({
                plotKey,
                ...plotData
            })) : []);
        
        return plotsToUse
            .map((plot) => {
                const dataEntry = plot.data_entry || {};
                const totalBA = parseFloat(dataEntry.total_ba) || 0;
                const pjp = parseFloat(plot.pjp) || 1;

                const calculateValue = (percentage) => {
                    const result = (totalBA / pjp) * 16666.67 * (percentage / 100);
                    return result;
                };

                // Ambil presentase dari setiap fase bunga
                const openHeartPercentage = calculateFaseBungaPercentage(plot, 'open_heart_fb_ba');
                const redBud125Percentage = calculateFaseBungaPercentage(plot, 'red_bud_125_fb_ba');
                const redBud254Percentage = calculateFaseBungaPercentage(plot, 'red_bud_254_fb_ba');
                const earlyConesPercentage = calculateFaseBungaPercentage(plot, 'early_cones_fb_ba');
                const midConesPercentage = calculateFaseBungaPercentage(plot, 'mid_cones_fb_ba');
                const lateConesPercentage = calculateFaseBungaPercentage(plot, 'late_cones_fb_ba');
                const earlyPetalsPercentage = calculateFaseBungaPercentage(plot, 'early_petals_fb_ba');
                const midPetalsPercentage = calculateFaseBungaPercentage(plot, 'mid_petals_fb_ba');
                const latePetalsPercentage = calculateFaseBungaPercentage(plot, 'late_petals_fb_ba');
                const dryPetalsPercentage = calculateFaseBungaPercentage(plot, 'dry_petals_fb_ba');
                const fillingStage1Percentage = calculateFaseBungaPercentage(plot, 'filling_stage_1_fb_ba');
                const fillingStage2Percentage = calculateFaseBungaPercentage(plot, 'filling_stage_2_fb_ba');
                const sc0Percentage = calculateFaseBungaPercentage(plot, 'sc0_fb_ba');
                const sc0LebihPercentage = calculateFaseBungaPercentage(plot, 'sc0_ld_fb_ba');

                // Hitung nilai dengan formula yang sama
                const openHeart = calculateValue(openHeartPercentage);
                const redBud125 = calculateValue(redBud125Percentage);
                const redBud254 = calculateValue(redBud254Percentage);
                const earlyCones = calculateValue(earlyConesPercentage);
                const midCones = calculateValue(midConesPercentage);
                const lateCones = calculateValue(lateConesPercentage);
                const earlyPetals = calculateValue(earlyPetalsPercentage);
                const midPetals = calculateValue(midPetalsPercentage);
                const latePetals = calculateValue(latePetalsPercentage);
                const dryPetals = calculateValue(dryPetalsPercentage);
                const fillingStage1 = calculateValue(fillingStage1Percentage);
                const fillingStage2 = calculateValue(fillingStage2Percentage);
                const sc0 = calculateValue(sc0Percentage);
                const sc0Lebih = calculateValue(sc0LebihPercentage);

                return {
                    plot: plot.plot || plot.plotKey,
                    luasPlot: plot.luas_plot || '-',
                    pjp: plot.pjp || '-',
                    dataEntry: dataEntry,
                    
                    // Simpan nilai asli untuk perhitungan tonase
                    nilaiAsli: {
                        openHeart: openHeart,
                        redBud125: redBud125,
                        redBud254: redBud254,
                        earlyCones: earlyCones,
                        midCones: midCones,
                        lateCones: lateCones,
                        earlyPetals: earlyPetals,
                        midPetals: midPetals,
                        latePetals: latePetals,
                        dryPetals: dryPetals,
                        fillingStage1: fillingStage1,
                        fillingStage2: fillingStage2,
                        sc0: sc0,
                        sc0Lebih: sc0Lebih
                    }
                };
            })
            .sort((a, b) => {
                if (!isNaN(a.plot) && !isNaN(b.plot)) {
                    return parseInt(a.plot) - parseInt(b.plot);
                }
                return a.plot.localeCompare(b.plot);
            });
    };

    // FUNGSI UNTUK MENGHITUNG TONASE BUAH PER PLOT
    const calculateTonasePerPlot = () => {
        const populationData = calculatePopulationPerHa();
        
        return populationData
            .map((populasiItem) => {
                const nilaiAsli = populasiItem.nilaiAsli || {};
                const luasPlot = parseFloat(populasiItem.luasPlot) || 0;
                
                // Formula tonase per plot: nilai_populasi * (1.3 / 1000) * luas_plot
                const calculateTonaseValue = (nilaiPopulasi) => {
                    return (nilaiPopulasi || 0) * (1.3 / 1000) * luasPlot;
                };

                // Hitung tonase per plot menggunakan nilai asli dari perhitungan populasi
                const openHeart = calculateTonaseValue(nilaiAsli.openHeart);
                const redBud125 = calculateTonaseValue(nilaiAsli.redBud125);
                const redBud254 = calculateTonaseValue(nilaiAsli.redBud254);
                const earlyCones = calculateTonaseValue(nilaiAsli.earlyCones);
                const midCones = calculateTonaseValue(nilaiAsli.midCones);
                const lateCones = calculateTonaseValue(nilaiAsli.lateCones);
                const earlyPetals = calculateTonaseValue(nilaiAsli.earlyPetals);
                const midPetals = calculateTonaseValue(nilaiAsli.midPetals);
                const latePetals = calculateTonaseValue(nilaiAsli.latePetals);
                const dryPetals = calculateTonaseValue(nilaiAsli.dryPetals);
                const fillingStage1 = calculateTonaseValue(nilaiAsli.fillingStage1);
                const fillingStage2 = calculateTonaseValue(nilaiAsli.fillingStage2);
                const sc0 = calculateTonaseValue(nilaiAsli.sc0);
                const sc0Lebih = calculateTonaseValue(nilaiAsli.sc0Lebih);
                
                // Hitung total tonase per plot
                const total = openHeart + redBud125 + redBud254 + earlyCones + midCones + 
                            lateCones + earlyPetals + midPetals + latePetals + dryPetals + 
                            fillingStage1 + fillingStage2 + sc0 + sc0Lebih;

                // Fungsi untuk format nilai tonase (ubah 0.00 menjadi -)
                const formatTonaseValue = (value) => {
                    const numValue = parseFloat(value);
                    if (numValue === 0 || isNaN(numValue)) {
                        return '-';
                    }
                    return numValue.toFixed(2);
                };

                return {
                    plot: populasiItem.plot,
                    luasPlot: populasiItem.luasPlot,
                    pjp: populasiItem.pjp,
                    dataEntry: populasiItem.dataEntry,
                    
                    // Format hasil tonase per plot dengan 2 desimal dan ubah 0.00 menjadi -
                    openHeart: formatTonaseValue(openHeart),
                    redBud125: formatTonaseValue(redBud125),
                    redBud254: formatTonaseValue(redBud254),
                    earlyCones: formatTonaseValue(earlyCones),
                    midCones: formatTonaseValue(midCones),
                    lateCones: formatTonaseValue(lateCones),
                    earlyPetals: formatTonaseValue(earlyPetals),
                    midPetals: formatTonaseValue(midPetals),
                    latePetals: formatTonaseValue(latePetals),
                    dryPetals: formatTonaseValue(dryPetals),
                    fillingStage1: formatTonaseValue(fillingStage1),
                    fillingStage2: formatTonaseValue(fillingStage2),
                    sc0: formatTonaseValue(sc0),
                    sc0Lebih: formatTonaseValue(sc0Lebih),
                    total: formatTonaseValue(total),
                    terpetik: '-',
                    dimakanTikus: '-'
                };
            });
    };

    // FUNGSI BARU: Menghitung total dari semua plot dengan formula yang sama untuk Tonase Per Plot
    const calculateTotalRow = (tonaseData) => {
        if (tonaseData.length === 0) return null;

        // Kumpulkan data total dari semua plot
        let totalBA = 0;
        let totalPJP = 0;
        let totalLuasPlot = 0;

        // Data untuk menghitung presentase total
        let totalFaseBunga = {
            open_heart_fb_ba: 0,
            red_bud_125_fb_ba: 0,
            red_bud_254_fb_ba: 0,
            early_cones_fb_ba: 0,
            mid_cones_fb_ba: 0,
            late_cones_fb_ba: 0,
            early_petals_fb_ba: 0,
            mid_petals_fb_ba: 0,
            late_petals_fb_ba: 0,
            dry_petals_fb_ba: 0,
            filling_stage_1_fb_ba: 0,
            filling_stage_2_fb_ba: 0,
            sc0_fb_ba: 0,
            sc0_ld_fb_ba: 0
        };

        let overallTotalBA = 0;

        // Kumpulkan data dari semua plot
        tonaseData.forEach(item => {
            const dataEntry = item.dataEntry || {};
            const faseBunga = dataEntry.fase_bunga || {};
            
            const plotTotalBA = parseFloat(dataEntry.total_ba) || 0;
            const plotPJP = parseFloat(item.pjp) || 0;
            const plotLuasPlot = parseFloat(item.luasPlot) || 0;

            totalBA += plotTotalBA;
            totalPJP += plotPJP;
            totalLuasPlot += plotLuasPlot;
            overallTotalBA += plotTotalBA;

            // Kumpulkan data fase bunga
            Object.keys(totalFaseBunga).forEach(key => {
                totalFaseBunga[key] += parseFloat(faseBunga[key]) || 0;
            });
        });

        // Hitung presentase total untuk setiap kategori
        const calculateTotalPercentage = (fieldValue) => {
            if (overallTotalBA > 0) {
                return (fieldValue / overallTotalBA) * 100;
            }
            return 0;
        };

        // Hitung nilai dengan formula yang sama: (total_ba / pjp * 16666.67 * presentase)
        const calculateTotalValue = (percentage) => {
            if (totalPJP > 0) {
                const result = (totalBA / totalPJP) * 16666.67 * (percentage / 100);
                return result; // TIDAK DIBAGI 1000
            }
            return 0;
        };

        // Hitung presentase total untuk setiap fase bunga
        const openHeartPercentage = calculateTotalPercentage(totalFaseBunga.open_heart_fb_ba);
        const redBud125Percentage = calculateTotalPercentage(totalFaseBunga.red_bud_125_fb_ba);
        const redBud254Percentage = calculateTotalPercentage(totalFaseBunga.red_bud_254_fb_ba);
        const earlyConesPercentage = calculateTotalPercentage(totalFaseBunga.early_cones_fb_ba);
        const midConesPercentage = calculateTotalPercentage(totalFaseBunga.mid_cones_fb_ba);
        const lateConesPercentage = calculateTotalPercentage(totalFaseBunga.late_cones_fb_ba);
        const earlyPetalsPercentage = calculateTotalPercentage(totalFaseBunga.early_petals_fb_ba);
        const midPetalsPercentage = calculateTotalPercentage(totalFaseBunga.mid_petals_fb_ba);
        const latePetalsPercentage = calculateTotalPercentage(totalFaseBunga.late_petals_fb_ba);
        const dryPetalsPercentage = calculateTotalPercentage(totalFaseBunga.dry_petals_fb_ba);
        const fillingStage1Percentage = calculateTotalPercentage(totalFaseBunga.filling_stage_1_fb_ba);
        const fillingStage2Percentage = calculateTotalPercentage(totalFaseBunga.filling_stage_2_fb_ba);
        const sc0Percentage = calculateTotalPercentage(totalFaseBunga.sc0_fb_ba);
        const sc0LebihPercentage = calculateTotalPercentage(totalFaseBunga.sc0_ld_fb_ba);

        // Hitung nilai total Per Ha dengan formula yang sama
        const openHeartPerHa = calculateTotalValue(openHeartPercentage);
        const redBud125PerHa = calculateTotalValue(redBud125Percentage);
        const redBud254PerHa = calculateTotalValue(redBud254Percentage);
        const earlyConesPerHa = calculateTotalValue(earlyConesPercentage);
        const midConesPerHa = calculateTotalValue(midConesPercentage);
        const lateConesPerHa = calculateTotalValue(lateConesPercentage);
        const earlyPetalsPerHa = calculateTotalValue(earlyPetalsPercentage);
        const midPetalsPerHa = calculateTotalValue(midPetalsPercentage);
        const latePetalsPerHa = calculateTotalValue(latePetalsPercentage);
        const dryPetalsPerHa = calculateTotalValue(dryPetalsPercentage);
        const fillingStage1PerHa = calculateTotalValue(fillingStage1Percentage);
        const fillingStage2PerHa = calculateTotalValue(fillingStage2Percentage);
        const sc0PerHa = calculateTotalValue(sc0Percentage);
        const sc0LebihPerHa = calculateTotalValue(sc0LebihPercentage);

        // Hitung tonase total per plot dengan formula: nilai_populasi * (1.3 / 1000) * total_luas_plot
        const calculateTotalTonase = (nilaiPopulasi) => {
            return (nilaiPopulasi || 0) * (1.3 / 1000) * totalLuasPlot;
        };

        const openHeartTonase = calculateTotalTonase(openHeartPerHa);
        const redBud125Tonase = calculateTotalTonase(redBud125PerHa);
        const redBud254Tonase = calculateTotalTonase(redBud254PerHa);
        const earlyConesTonase = calculateTotalTonase(earlyConesPerHa);
        const midConesTonase = calculateTotalTonase(midConesPerHa);
        const lateConesTonase = calculateTotalTonase(lateConesPerHa);
        const earlyPetalsTonase = calculateTotalTonase(earlyPetalsPerHa);
        const midPetalsTonase = calculateTotalTonase(midPetalsPerHa);
        const latePetalsTonase = calculateTotalTonase(latePetalsPerHa);
        const dryPetalsTonase = calculateTotalTonase(dryPetalsPerHa);
        const fillingStage1Tonase = calculateTotalTonase(fillingStage1PerHa);
        const fillingStage2Tonase = calculateTotalTonase(fillingStage2PerHa);
        const sc0Tonase = calculateTotalTonase(sc0PerHa);
        const sc0LebihTonase = calculateTotalTonase(sc0LebihPerHa);

        // Hitung total keseluruhan
        const totalTonase = openHeartTonase + redBud125Tonase + redBud254Tonase + earlyConesTonase + 
                          midConesTonase + lateConesTonase + earlyPetalsTonase + midPetalsTonase + 
                          latePetalsTonase + dryPetalsTonase + fillingStage1Tonase + fillingStage2Tonase + 
                          sc0Tonase + sc0LebihTonase;

        return {
            plot: 'Total',
            luasPlot: formatAngkaTotal(totalLuasPlot),
            pjp: formatPJP(totalPJP), // GUNAKAN formatPJP untuk total juga
            
            // Data fase bunga - GUNAKAN formatAngkaTotal untuk 2 digit
            openHeart: formatAngkaTotal(openHeartTonase),
            redBud125: formatAngkaTotal(redBud125Tonase),
            redBud254: formatAngkaTotal(redBud254Tonase),
            earlyCones: formatAngkaTotal(earlyConesTonase),
            midCones: formatAngkaTotal(midConesTonase),
            lateCones: formatAngkaTotal(lateConesTonase),
            earlyPetals: formatAngkaTotal(earlyPetalsTonase),
            midPetals: formatAngkaTotal(midPetalsTonase),
            latePetals: formatAngkaTotal(latePetalsTonase),
            dryPetals: formatAngkaTotal(dryPetalsTonase),
            fillingStage1: formatAngkaTotal(fillingStage1Tonase),
            fillingStage2: formatAngkaTotal(fillingStage2Tonase),
            sc0: formatAngkaTotal(sc0Tonase),
            sc0Lebih: formatAngkaTotal(sc0LebihTonase),
            
            // Total Buah Alami
            total: formatAngkaTotal(totalTonase),
            terpetik: '-',
            dimakanTikus: '-'
        };
    };

    const tonaseData = calculateTonasePerPlot();
    const totalData = calculateTotalRow(tonaseData);

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Data Tonase Buah Per Plot
            </Typography>
            
            <Card>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TonaseBuahPerHaPerPlotTableHeader />
                        <TableBody>
                            {tonaseData.map((item, index) => (
                                <TableRow key={index}>
                                    {/* Plot */}
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                                        {item.plot}
                                    </TableCell>
                                    
                                    {/* Luas */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {item.luasPlot}
                                    </TableCell>
                                    
                                    {/* PJP - GUNAKAN formatPJP di sini */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                        {formatPJP(item.pjp)}
                                    </TableCell>
                                    
                                    {/* Fase Bunga - Data */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.openHeart}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.redBud125}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.redBud254}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.earlyCones}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.midCones}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.lateCones}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.earlyPetals}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.midPetals}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.latePetals}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.dryPetals}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.fillingStage1}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.fillingStage2}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.sc0}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.sc0Lebih}
                                    </TableCell>
                                    
                                    {/* Total Buah Alami */}
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }} align="right">
                                        {item.total}
                                    </TableCell>

                                </TableRow>
                            ))}
                            
                            {/* ROW TOTAL */}
                            {totalData && (
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                                        {totalData.plot}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                                        {totalData.luasPlot}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                                        {totalData.pjp}
                                    </TableCell>
                                    
                                    {/* Fase Bunga - Total */}
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.openHeart}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.redBud125}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.redBud254}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.earlyCones}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.midCones}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.lateCones}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.earlyPetals}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.midPetals}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.latePetals}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.dryPetals}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.fillingStage1}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.fillingStage2}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.sc0}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.sc0Lebih}
                                    </TableCell>
                                    
                                    {/* Total Buah Alami - Total */}
                                    <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }} align="right">
                                        {totalData.total}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                {tonaseData.length === 0 && (
                    <Typography color="text.secondary" fontStyle="italic" sx={{ mt: 2 }}>
                        Tidak ada data tonase buah per plot
                    </Typography>
                )}
            </Card>
        </Box>
    );
};

export default TonaseBuahPerPlotTab;